/**
 * Leaderboard UI Component
 * Displays player rankings with ELO, stats, and rank badges
 */

import { leaderboardService, type LeaderboardEntry, type TimeFilter, type RankBadge } from '../../social';
import { authService } from '../../auth';
import { escapeHtml, renderAvatar, renderLoading, renderEmpty } from '../utils';

export interface LeaderboardOptions {
  container: HTMLElement;
  pageSize?: number;
  onPlayerClick?: (userId: string) => void;
}

export class Leaderboard {
  private container: HTMLElement;
  private pageSize: number;
  private currentPage: number = 0;
  private totalEntries: number = 0;
  private currentFilter: TimeFilter = 'all';
  private onPlayerClick?: (userId: string) => void;
  private currentUserId: string | null = null;

  constructor(options: LeaderboardOptions) {
    this.container = options.container;
    this.pageSize = options.pageSize || 25;
    this.onPlayerClick = options.onPlayerClick;
    
    const user = authService.getCurrentUser();
    this.currentUserId = user?.id || null;

    this.render();
    this.loadData();
  }

  private render(): void {
    this.container.innerHTML = `
      <div class="leaderboard">
        <div class="leaderboard-header">
          <h2 class="leaderboard-title">Leaderboard</h2>
          <div class="leaderboard-filters">
            <button class="leaderboard-filter active" data-filter="all">All Time</button>
            <button class="leaderboard-filter" data-filter="month">This Month</button>
            <button class="leaderboard-filter" data-filter="week">This Week</button>
          </div>
        </div>
        
        <div class="leaderboard-table-container">
          <table class="leaderboard-table">
            <thead>
              <tr>
                <th class="col-rank">Rank</th>
                <th class="col-player">Player</th>
                <th class="col-elo">ELO</th>
                <th class="col-record">W/L/D</th>
                <th class="col-winrate">Win Rate</th>
              </tr>
            </thead>
            <tbody class="leaderboard-body">
              <tr class="leaderboard-loading">
                <td colspan="5">Loading...</td>
              </tr>
            </tbody>
          </table>
        </div>
        
        <div class="leaderboard-pagination">
          <button class="leaderboard-page-btn prev" disabled>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M15 18l-6-6 6-6"/>
            </svg>
            Previous
          </button>
          <span class="leaderboard-page-info">Page 1</span>
          <button class="leaderboard-page-btn next" disabled>
            Next
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M9 18l6-6-6-6"/>
            </svg>
          </button>
        </div>
      </div>
    `;

    this.attachEventListeners();
  }

  private attachEventListeners(): void {
    // Filter buttons
    const filters = this.container.querySelectorAll('.leaderboard-filter');
    filters.forEach(btn => {
      btn.addEventListener('click', () => {
        filters.forEach(f => f.classList.remove('active'));
        btn.classList.add('active');
        this.currentFilter = btn.getAttribute('data-filter') as TimeFilter;
        this.currentPage = 0;
        this.loadData();
      });
    });

    // Pagination
    const prevBtn = this.container.querySelector('.leaderboard-page-btn.prev') as HTMLButtonElement;
    const nextBtn = this.container.querySelector('.leaderboard-page-btn.next') as HTMLButtonElement;

    prevBtn?.addEventListener('click', () => {
      if (this.currentPage > 0) {
        this.currentPage--;
        this.loadData();
      }
    });

    nextBtn?.addEventListener('click', () => {
      const maxPage = Math.ceil(this.totalEntries / this.pageSize) - 1;
      if (this.currentPage < maxPage) {
        this.currentPage++;
        this.loadData();
      }
    });
  }

  private async loadData(): Promise<void> {
    const tbody = this.container.querySelector('.leaderboard-body') as HTMLTableSectionElement;
    tbody.innerHTML = '<tr class="leaderboard-loading"><td colspan="5">Loading...</td></tr>';

    const offset = this.currentPage * this.pageSize;
    const { entries, total } = await leaderboardService.getLeaderboard(
      this.pageSize,
      offset,
      this.currentFilter
    );

    this.totalEntries = total;
    this.renderEntries(entries);
    this.updatePagination();
  }

  private renderEntries(entries: LeaderboardEntry[]): void {
    const tbody = this.container.querySelector('.leaderboard-body') as HTMLTableSectionElement;

    if (entries.length === 0) {
      tbody.innerHTML = `
        <tr class="leaderboard-empty">
          <td colspan="5">No ranked games played yet. Play online games to appear on the leaderboard!</td>
        </tr>
      `;
      return;
    }

    tbody.innerHTML = entries.map(entry => this.renderRow(entry)).join('');

    // Attach row click handlers
    const rows = tbody.querySelectorAll('.leaderboard-row');
    rows.forEach(row => {
      row.addEventListener('click', () => {
        const userId = row.getAttribute('data-user-id');
        if (userId && this.onPlayerClick) {
          this.onPlayerClick(userId);
        }
      });
    });
  }

  private renderRow(entry: LeaderboardEntry): string {
    const badge = leaderboardService.getRankBadge(entry.eloRating);
    const winRate = leaderboardService.calculateWinRate(entry.wins, entry.gamesPlayed);
    const isCurrentUser = entry.userId === this.currentUserId;
    const avatarHtml = renderAvatar(entry.avatarUrl, entry.username, entry.displayName, 'sm');

    return `
      <tr class="leaderboard-row ${isCurrentUser ? 'current-user' : ''}" data-user-id="${entry.userId}">
        <td class="col-rank">
          ${this.renderRankNumber(entry.rank)}
        </td>
        <td class="col-player">
          <div class="leaderboard-player">
            ${avatarHtml}
            <div class="leaderboard-player-info">
              <span class="leaderboard-username">${escapeHtml(entry.displayName || entry.username)}</span>
              ${entry.displayName ? `<span class="leaderboard-handle">@${escapeHtml(entry.username)}</span>` : ''}
            </div>
          </div>
        </td>
        <td class="col-elo">
          <div class="leaderboard-elo">
            ${this.renderRankBadge(badge)}
            <span class="elo-value">${entry.eloRating}</span>
          </div>
        </td>
        <td class="col-record">
          <span class="record-wins">${entry.wins}</span>
          <span class="record-separator">/</span>
          <span class="record-losses">${entry.losses}</span>
          <span class="record-separator">/</span>
          <span class="record-draws">${entry.draws}</span>
        </td>
        <td class="col-winrate">
          <div class="winrate-container">
            <div class="winrate-bar" style="width: ${winRate}%"></div>
            <span class="winrate-value">${winRate}%</span>
          </div>
        </td>
      </tr>
    `;
  }

  private renderRankNumber(rank: number): string {
    if (rank === 1) {
      return '<span class="rank-medal gold">🥇</span>';
    } else if (rank === 2) {
      return '<span class="rank-medal silver">🥈</span>';
    } else if (rank === 3) {
      return '<span class="rank-medal bronze">🥉</span>';
    }
    return `<span class="rank-number">#${rank}</span>`;
  }

  private renderRankBadge(badge: RankBadge): string {
    return `<span class="rank-badge" style="background-color: ${badge.color}">${badge.tier}</span>`;
  }

  private updatePagination(): void {
    const prevBtn = this.container.querySelector('.leaderboard-page-btn.prev') as HTMLButtonElement;
    const nextBtn = this.container.querySelector('.leaderboard-page-btn.next') as HTMLButtonElement;
    const pageInfo = this.container.querySelector('.leaderboard-page-info') as HTMLSpanElement;

    const maxPage = Math.ceil(this.totalEntries / this.pageSize);
    const currentPageDisplay = this.currentPage + 1;

    prevBtn.disabled = this.currentPage === 0;
    nextBtn.disabled = this.currentPage >= maxPage - 1 || maxPage === 0;

    pageInfo.textContent = maxPage > 0 
      ? `Page ${currentPageDisplay} of ${maxPage}`
      : 'Page 1';
  }

  /**
   * Refresh the leaderboard data
   */
  refresh(): void {
    this.loadData();
  }

  /**
   * Update current user (call after login/logout)
   */
  updateCurrentUser(): void {
    const user = authService.getCurrentUser();
    this.currentUserId = user?.id || null;
    this.loadData();
  }

  /**
   * Destroy the component and clean up
   */
  destroy(): void {
    this.container.innerHTML = '';
  }
}
